import React, { useState, useEffect } from 'react';
import './RatingForm.css';

const RatingForm = ({ whisky, existingRating, onSubmit, onCancel }) => {
  const [ratings, setRatings] = useState({
    nose: existingRating?.nose || 0,
    palate: existingRating?.palate || 0,
    finish: existingRating?.finish || 0,
    overall: existingRating?.overall || 0
  });
  const [comments, setComments] = useState(existingRating?.comments || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cookie utility functions
  const setCookie = (name, value, days = 7) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        try {
          return JSON.parse(decodeURIComponent(c.substring(nameEQ.length, c.length)));
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  };

  const deleteCookie = (name) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  };

  // Auto-save draft to cookies
  useEffect(() => {
    const draftKey = `whisky_draft_${whisky.id}`;
    const draft = { ratings, comments };
    setCookie(draftKey, draft, 7); // Keep drafts for 7 days
  }, [ratings, comments, whisky.id]);

  // Load draft on mount
  useEffect(() => {
    if (!existingRating) {
      const draftKey = `whisky_draft_${whisky.id}`;
      const savedDraft = getCookie(draftKey);
      if (savedDraft) {
        setRatings(prevRatings => savedDraft.ratings || prevRatings);
        setComments(savedDraft.comments || '');
      }
    }
  }, [whisky.id, existingRating]);

  const handleRatingChange = (category, value) => {
    setRatings(prev => ({
      ...prev,
      [category]: parseInt(value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate brief loading for better UX
    await new Promise(resolve => setTimeout(resolve, 300));
    
    onSubmit(whisky.id, {
      ...ratings,
      comments: comments.trim()
    });
    
    // Clear draft
    const draftKey = `whisky_draft_${whisky.id}`;
    deleteCookie(draftKey);
    
    setIsSubmitting(false);
  };

  const totalScore = Object.values(ratings).reduce((sum, rating) => sum + rating, 0);
  const averageScore = totalScore / 4;

  const getRatingColor = (value) => {
    if (value >= 20) return '#4CAF50'; // Green
    if (value >= 15) return '#FFC107'; // Amber
    if (value >= 10) return '#FF9800'; // Orange
    if (value >= 5) return '#FF5722';  // Red-orange
    return '#F44336'; // Red
  };

  const getRatingLabel = (value) => {
    if (value >= 23) return 'Exceptional';
    if (value >= 20) return 'Excellent';
    if (value >= 17) return 'Very Good';
    if (value >= 14) return 'Good';
    if (value >= 11) return 'Average';
    if (value >= 8) return 'Below Average';
    if (value >= 5) return 'Poor';
    if (value >= 1) return 'Very Poor';
    return 'Not Rated';
  };

  return (
    <div className="rating-form">
      <div className="form-header">
        <button className="back-btn" onClick={onCancel}>
          ← Back
        </button>
        <h2>Rate Whisky</h2>
        <div className="score-display">
          <span className="total-score">{totalScore}/100</span>
          <span className="average-score">Avg: {averageScore.toFixed(1)}/25</span>
        </div>
      </div>

      <div className={`whisky-info ${whisky.dreamDram ? 'dream-dram' : ''}`}>
        <h3>
          {whisky.name}
          {whisky.dreamDram && (
            <span className="dream-dram-icon" title={`Dream Dram - Requires ${whisky.dreamDramTokens} token${whisky.dreamDramTokens > 1 ? 's' : ''}`}>
              💎{whisky.dreamDramTokens}
            </span>
          )}
        </h3>
        {whisky.distillery && <p className="distillery">{whisky.distillery}</p>}
        <div className="whisky-meta">
          {whisky.region && <span>{whisky.region}</span>}
          {whisky.age && <span>{whisky.age}</span>}
          {whisky.abv && <span>{whisky.abv}% ABV</span>}
        </div>
        {whisky.dreamDram && (
          <div className="dream-dram-notice">
            <span className="dream-dram-badge">💎 Dream Dram</span>
            <p>This premium tasting requires <strong>{whisky.dreamDramTokens}</strong> Dream Dram token{whisky.dreamDramTokens > 1 ? 's' : ''}</p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="rating-form-content">
        <div className="rating-categories">
          {[
            { key: 'nose', label: 'Nose', icon: '👃' },
            { key: 'palate', label: 'Palate', icon: '👅' },
            { key: 'finish', label: 'Finish', icon: '🔚' },
            { key: 'overall', label: 'Overall Impression', icon: '⭐' }
          ].map(({ key, label, icon }) => (
            <div key={key} className="rating-category">
              <div className="category-header">
                <span className="category-icon">{icon}</span>
                <span className="category-label">{label}</span>
                <div className="category-score">
                  <span 
                    className="score-value"
                    style={{ color: getRatingColor(ratings[key]) }}
                  >
                    {ratings[key]}
                  </span>
                  <span className="score-max">/25</span>
                </div>
              </div>
              
              <div className="slider-container">
                <input
                  type="range"
                  min="0"
                  max="25"
                  value={ratings[key]}
                  onChange={(e) => handleRatingChange(key, e.target.value)}
                  className="rating-slider"
                  style={{
                    background: `linear-gradient(to right, ${getRatingColor(ratings[key])} 0%, ${getRatingColor(ratings[key])} ${(ratings[key] / 25) * 100}%, rgba(255,255,255,0.2) ${(ratings[key] / 25) * 100}%, rgba(255,255,255,0.2) 100%)`
                  }}
                />
                <div className="slider-labels">
                  <span>0</span>
                  <span className="rating-label">{getRatingLabel(ratings[key])}</span>
                  <span>25</span>
                </div>
              </div>

              <div className="quick-scores">
                {[5, 10, 15, 20, 25].map(score => (
                  <button
                    key={score}
                    type="button"
                    className={`quick-score ${ratings[key] === score ? 'active' : ''}`}
                    onClick={() => handleRatingChange(key, score)}
                  >
                    {score}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="comments-section">
          <label htmlFor="comments" className="comments-label">
            <span>💭</span>
            Comments (Optional)
          </label>
          <textarea
            id="comments"
            className="comments-input"
            placeholder="Tasting notes, thoughts, memorable characteristics..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || totalScore === 0}
          >
            {isSubmitting ? (
              <span className="loading-text">
                <span className="spinner-small"></span>
                Saving...
              </span>
            ) : (
              existingRating ? 'Update Rating' : 'Save Rating'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RatingForm;
